// SPDX-License-Identifier: LicenseRef-Syncanix-Proprietary

/**
 * The house treatment for an inline prose link.
 *
 * ## Why it is a constant
 *
 * It was hand-repeated in seven places across four spellings that differed
 * only in whether they carried the hover — so three of the seven gave no
 * feedback when pointed at, and nothing said which was the intended one. A
 * further ten links, all on `/trust`, were bare `font-medium underline`: the
 * inherited body colour, no link token, and no hover at all. That is the
 * procurement landing page, and its links did not look or behave like links
 * anywhere else on the site.
 *
 * Found by measuring rather than reading — hovering every interactive element
 * and diffing the computed style of it and its subtree. `ui-design.md` § 3 has
 * always required a visible hover state; nothing had ever checked.
 *
 * ## The colour
 *
 * `--bd-link` (#2348db, 6.44:1 on white), NOT `--bd-blue`: the brand blue is a
 * FILL at 3.1:1, a WCAG AA failure at body size (`ui-design.md` § "`primary`
 * is a FILL, `link` is TEXT").
 *
 * The underline starts at 30% of the link colour and goes solid on hover, so
 * the feedback is a change in the decoration rather than in the text itself —
 * text that changes colour under the pointer is harder to read at the moment
 * the reader is looking straight at it.
 */
export const INLINE_LINK =
  'font-medium text-[color:var(--bd-link)] underline decoration-[color:color-mix(in_srgb,var(--bd-link)_30%,transparent)] underline-offset-[3px] transition-colors hover:decoration-[color:var(--bd-link)]';

/**
 * The same, plus the keyboard focus ring — for a link that is a standalone
 * call to action rather than one inside a sentence.
 *
 * `rounded-sm` so the ring has a shape to follow; without it the ring traces
 * the text box's square corners and reads as a box rather than a highlight.
 */
export const INLINE_LINK_FOCUSABLE = `rounded-sm ${INLINE_LINK} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2`;
/**
 * The keyboard focus ring every standalone link carries.
 *
 * Split out and shared rather than repeated, because repeating it is how the
 * site ended up with THREE different answers to the same question. Measured
 * across the eleven standalone links outside prose:
 *
 * - the four "back to X" breadcrumbs carried this full ring;
 * - the four inline-flex ink links carried `focus-visible:underline` and no
 *   ring at all;
 * - the three quiet blue links on the landing carried nothing, falling back to
 *   whatever the browser draws.
 *
 * None of that was a decision. The three shapes appear in files written weeks
 * apart, and the only thing distinguishing them is which one the author had
 * open at the time — the same drift that produced four spellings of the inline
 * link and left three of them featureless.
 *
 * `rounded-sm` so the ring has a shape to follow: without it the ring traces
 * the text box's square corners and reads as a box rather than a highlight.
 */
const LINK_FOCUS = [
  'rounded-sm focus-visible:outline-none focus-visible:ring-2',
  'focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2',
].join(' ');

/**
 * The breadcrumb back to a parent document — `← Pricing`, `← Trust Center`.
 *
 * Inherits its colour rather than declaring one, because it lives inside a
 * `PageHero` eyebrow whose tone is already set; going to ink on hover is the
 * cue, since an eyebrow that is already muted cannot get quieter.
 */
export const BACK_LINK = `${LINK_FOCUS} underline-offset-4 transition-colors hover:text-[color:var(--bd-ink)] hover:underline`;

/**
 * A standalone forward link in the page's own ink — "see the full price list",
 * "compare build vs buy".
 *
 * Colour, underline and focus ONLY; the call site owns layout. Four of the five
 * sites are `inline-flex items-center gap-1 text-sm` because they carry a
 * trailing arrow that must sit on the text's centre line rather than its
 * baseline, and the fifth is a plain link inside a paragraph. Folding that
 * layout in here would have silently resized the fifth — the constant would
 * have been imposing a shape rather than sharing an affordance, which is the
 * opposite of why it exists.
 */
export const ACTION_LINK = `${LINK_FOCUS} font-medium text-[color:var(--bd-ink)] underline-offset-4 hover:underline`;

/**
 * The quiet blue link that closes a landing section — "read the docs →".
 *
 * No underline at rest, unlike `INLINE_LINK`: this one sits alone under a block
 * of content where there is nothing to distinguish it FROM, so the underline is
 * the hover cue rather than the affordance. Inside a sentence that reasoning
 * inverts, which is why the two are separate constants and not one with a flag.
 */
export const QUIET_LINK = `${LINK_FOCUS} font-medium text-[color:var(--bd-link)] underline-offset-4 hover:underline`;
